/* src/modules/hr/HRPortal.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, addDoc, updateDoc, deleteDoc, serverTimestamp, orderBy, where } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { UploadIcon, LinkIcon, EditIcon, TrashIcon, PaperclipIcon } from '../../components/icons/Icons';
import EmployeeDirectory from './EmployeeDirectory';
import HolidayManagement from './HolidayManagement';
import ExitManagement from './ExitManagement';
import CareerProgressionReport from './CareerProgressionReport';
import OrgChart from './OrgChart'; 

// ===================================================================
// ## Main HR Portal Component
// ===================================================================
export default function HRPortal({ appId, userProfile, initialTab }) {
    // ✅ 1. CHANGE DEFAULT TAB: Changed fallback from 'policies' to 'directory'
    const [activeTab, setActiveTab] = useState(initialTab || 'directory');
    const canManage = userProfile?.role === 'Admin' || userProfile?.department === 'HR';

    // ✅ 2. FIX SCROLL POSITION: Scrolls to top whenever tab changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeTab]);

    let currentViewComponent;
    switch (activeTab) {
        case 'forms':
            currentViewComponent = <DocumentView appId={appId} userProfile={userProfile} canManage={canManage} category="forms" />;
            break;
        case 'training':
            currentViewComponent = <DocumentView appId={appId} userProfile={userProfile} canManage={canManage} category="training" />;
            break;
        case 'directory':
            currentViewComponent = <EmployeeDirectory appId={appId} userProfile={userProfile} canManage={canManage} />;
            break;
        case 'holidays':
            currentViewComponent = <HolidayManagement appId={appId} canManage={canManage} />;
            break;
        case 'org_chart':
            currentViewComponent = <OrgChart appId={appId} />;
            break;
        case 'exit_management':
            currentViewComponent = <ExitManagement appId={appId} userProfile={userProfile} />;
            break;
	case 'progression':
            currentViewComponent = <CareerProgressionReport appId={appId} />;
            break;
        case 'policies':
        default:
            currentViewComponent = <DocumentView appId={appId} userProfile={userProfile} canManage={canManage} category="policies" />;
            break;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md min-h-screen print:p-0 print:shadow-none">
            <h1 className="text-2xl font-bold text-[#213368] mb-4 print:hidden">HR Center</h1>
            <div className="print:hidden">
                {/* --- ADDED canManage PROP HERE --- */}
                <HRNav activeView={activeTab} setView={setActiveTab} canManage={canManage} />
            </div>
            <div className="mt-6 print:mt-0">
                {currentViewComponent}
            </div>
        </div>
    );
}

// ===================================================================
// ## Tab Navigation
// ===================================================================
function HRNav({ activeView, setView, canManage }) {
    const NavButton = ({ viewName, children }) => {
        const isActive = activeView === viewName;
        return ( 
            <button 
                onClick={() => setView(viewName)} 
                className={`${ isActive ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700' } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
                {children}
            </button> 
        );
    };

    return (
        <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {/* Reordered buttons to match your likely preference, but logic handles the active state */}
                <NavButton viewName="directory">Employee Directory</NavButton>
                <NavButton viewName="org_chart">Org Chart</NavButton>
                <NavButton viewName="policies">Employee Policies</NavButton>
                <NavButton viewName="forms">Employee Forms</NavButton>
                <NavButton viewName="training">Training Materials</NavButton>
                <NavButton viewName="holidays">Holiday List</NavButton>
                <NavButton viewName="exit_management">Exit Management</NavButton>
                {canManage && (
                    <>
                        <NavButton viewName="progression">Career Progression</NavButton>
                    </>
                )}
            </nav>
        </div>
    );
}

// ===================================================================
// ## Document-related Components (Unchanged)
// ===================================================================
function DocumentView({ appId, userProfile, canManage, category }) {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documentToEdit, setDocumentToEdit] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);

    useEffect(() => {
        const docsCollectionRef = collection(db, `/artifacts/${appId}/public/data/hrDocuments`);
        const q = query(docsCollectionRef, where("category", "==", category));
        
        const unsub = onSnapshot(q, (snapshot) => {
            const docsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setDocuments(docsData);
            setIsLoading(false);
        });

        return () => unsub();
    }, [appId, category]);

    const handleOpenModal = (doc = null) => {
        setDocumentToEdit(doc);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setDocumentToEdit(null);
        setIsModalOpen(false);
    };
    
    const handleDeleteRequest = (doc) => {
        setDocumentToDelete(doc);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!documentToDelete) return;
        try {
            // STEP 1: Try to delete the file, but don't break if it's missing
            if (documentToDelete.type === 'document' && documentToDelete.storagePath) {
                try {
                    const fileRef = ref(storage, documentToDelete.storagePath);
                    await deleteObject(fileRef);
                } catch (storageError) {
                    // If the file is already gone (404), just log a warning and keep going
                    console.warn("Could not delete file from Storage (it might already be missing):", storageError.message);
                }
            }
            
            // STEP 2: Always delete the database record so it disappears from the UI
            await deleteDoc(doc(db, `/artifacts/${appId}/public/data/hrDocuments`, documentToDelete.id));
            
        } catch (error) {
            console.error("Error deleting document record from database:", error);
            alert("Failed to delete the document record.");
        } finally {
            setIsDeleteModalOpen(false);
            setDocumentToDelete(null);
        }
    };

    return (
        <div>
            <div className="flex justify-end mb-4">
                {canManage && (
                    <button onClick={() => handleOpenModal()} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 flex items-center">
                        <UploadIcon />
                        <span className="ml-2">Add New</span>
                    </button>
                )}
            </div>
            {isLoading ? <LoadingSpinner /> : (
                <DocumentList 
                    documents={documents} 
                    canManage={canManage}
                    onEdit={handleOpenModal}
                    onDelete={handleDeleteRequest}
                />
            )}
            {isModalOpen && (
                <UploadModal 
                    appId={appId}
                    userProfile={userProfile}
                    existingDoc={documentToEdit}
                    onClose={handleCloseModal}
                    category={category} 
                />
            )}
            {isDeleteModalOpen && (
                <DeleteConfirmModal 
                    title="Confirm Document Deletion"
                    message={`Are you sure you want to delete the document: ${documentToDelete?.title}?`}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </div>
    );
}

function DocumentList({ documents, canManage, onEdit, onDelete }) {
    const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'ascending' });

    const sortedDocuments = useMemo(() => {
        let sortableItems = [...documents];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (sortConfig.key === 'lastUpdatedAt') {
                    aValue = aValue?.toDate() || 0;
                    bValue = bValue?.toDate() || 0;
                }
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [documents, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const truncateDescription = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substr(0, maxLength) + '...';
    };

    if (documents.length === 0) {
        return <p className="text-center text-gray-500 py-8">No documents in this category.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">S.No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('title')}>
                            Title <SortIndicator direction={sortConfig.key === 'title' ? sortConfig.direction : null} />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document / Link</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('lastUpdatedAt')}>
                            Last Updated <SortIndicator direction={sortConfig.key === 'lastUpdatedAt' ? sortConfig.direction : null} />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated By</th>
                        {canManage && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {sortedDocuments.map((doc, index) => (
                        <tr key={doc.id}>
                            <td className="px-6 py-4">{index + 1}</td>
                            <td className="px-6 py-4 font-medium text-[#213368]">{doc.title}</td>
                            <td className="px-6 py-4 text-sm text-gray-600" title={doc.description}>{truncateDescription(doc.description)}</td>
                            <td className="px-6 py-4">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-semibold flex items-center">
                                    {doc.type === 'document' ? <PaperclipIcon /> : <LinkIcon />}
                                    <span className="ml-2 truncate" title={doc.fileName}>{doc.type === 'document' ? (doc.fileName || 'View File') : 'Open Link'}</span>
                                </a>
                            </td>
                            <td className="px-6 py-4 text-sm">{doc.lastUpdatedAt?.toDate().toLocaleDateString()}</td>
                            <td className="px-6 py-4 text-sm">{doc.updatedBy}</td>
                            {canManage && (
                                <td className="px-6 py-4">
                                    <div className="flex items-center space-x-2">
                                        <button onClick={() => onEdit(doc)} title="Edit" className="p-2 text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                        <button onClick={() => onDelete(doc)} title="Delete" className="p-2 text-red-500 hover:text-red-700"><TrashIcon /></button>
                                    </div>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function UploadModal({ appId, userProfile, existingDoc, onClose, category }) {
    const [title, setTitle] = useState(existingDoc?.title || '');
    const [description, setDescription] = useState(existingDoc?.description || '');
    const [type, setType] = useState(existingDoc?.type || 'document');
    const [url, setUrl] = useState(existingDoc?.url || '');
    const [file, setFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [attachmentRemoved, setAttachmentRemoved] = useState(false);

    const handleRemoveAttachment = () => setAttachmentRemoved(true);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title) { setMessage('Title is required.'); return; }
        setIsSaving(true);
        setMessage('Processing...');

        const docData = { title, description, type, updatedBy: userProfile.name, lastUpdatedAt: serverTimestamp(), category };

        try {
            if (type === 'document') {
                if (file) {
                    if (existingDoc?.storagePath) await deleteObject(ref(storage, existingDoc.storagePath));
                    const storagePath = `artifacts/${appId}/hr-documents/${Date.now()}_${file.name}`;
                    const uploadTask = uploadBytesResumable(ref(storage, storagePath), file);
                    uploadTask.on('state_changed', () => {}, (error) => { throw error; },
                        async () => {
                            docData.url = await getDownloadURL(uploadTask.snapshot.ref);
                            docData.fileName = file.name;
                            docData.storagePath = storagePath;
                            await saveToFirestore(docData);
                        }
                    );
                } else {
                    if (attachmentRemoved && existingDoc?.storagePath) {
                        await deleteObject(ref(storage, existingDoc.storagePath));
                        docData.url = null; docData.fileName = null; docData.storagePath = null;
                    }
                    await saveToFirestore(docData);
                }
            } else {
                docData.url = url;
                if (existingDoc?.storagePath) {
                    await deleteObject(ref(storage, existingDoc.storagePath));
                    docData.fileName = null; docData.storagePath = null;
                }
                await saveToFirestore(docData);
            }
        } catch (error) {
            console.error("Error saving document:", error);
            setMessage(`Error: ${error.message}`);
            setIsSaving(false);
        }
    };
    
    const saveToFirestore = async (dataToSave) => {
        if (existingDoc) {
            await updateDoc(doc(db, `/artifacts/${appId}/public/data/hrDocuments`, existingDoc.id), dataToSave);
        } else {
            await addDoc(collection(db, `/artifacts/${appId}/public/data/hrDocuments`), dataToSave);
        }
        setMessage(existingDoc ? 'Successfully updated!' : 'Successfully added!');
        setTimeout(onClose, 2000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-xl font-bold text-[#213368] mb-4">{existingDoc ? 'Edit' : 'Add New'} Document</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Type</label>
                        <div className="flex items-center space-x-4 mt-1">
                            <label><input type="radio" value="document" checked={type === 'document'} onChange={() => setType('document')} disabled={isSaving}/> Document</label>
                            <label><input type="radio" value="link" checked={type === 'link'} onChange={() => setType('link')} disabled={isSaving}/> Link</label>
                        </div>
                    </div>
                    <div className="mb-4">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
                        <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full p-2 mt-1 border rounded" required />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full p-2 mt-1 border rounded" rows="3"></textarea>
                    </div>
                    {type === 'document' ? (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Attachment</label>
                            {existingDoc?.fileName && !attachmentRemoved && !file ? (
                                <div className="flex items-center justify-between p-2 bg-white border rounded-md">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <PaperclipIcon />
                                        <span className="ml-2 truncate">{existingDoc.fileName}</span>
                                    </div>
                                    <button type="button" onClick={handleRemoveAttachment} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Remove</button>
                                </div>
                            ) : (
                                <input id="file" type="file" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm" />
                            )}
                        </div>
                    ) : (
                        <div className="mb-4">
                            <label htmlFor="url" className="block text-sm font-medium text-gray-700">URL</label>
                            <input id="url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} className="w-full p-2 mt-1 border rounded" required placeholder="https://..." />
                        </div>
                    )}
                    <div className="h-6 mt-4">
                        {message && <p className="text-sm text-center font-medium">{message}</p>}
                    </div>
                    <div className="flex justify-end space-x-4 mt-6">
                        <button type="button" onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-6 py-2 w-32 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 flex justify-center items-center">
                            {isSaving ? 'Saving...' : (existingDoc ? 'Save Changes' : 'Add')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const SortIndicator = ({ direction }) => {
    if (!direction) return null;
    return direction === 'ascending' ? ' ▲' : ' ▼';
};

function DeleteConfirmModal({ title, message, onClose, onConfirm }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-red-600 mb-4">{title}</h2>
                <p className="text-gray-600 mb-6">{message}</p>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700">
                        Confirm Deletion
                    </button>
                </div>
            </div>
        </div>
    );
}